import {ChangeDetectionStrategy, Component, OnInit, signal} from '@angular/core';
import {ButtonModule} from "primeng/button";
import {MessageService} from "primeng/api";
import {TableModule} from "primeng/table";
import {Tag} from "primeng/tag";
import {ProgressSpinner} from "primeng/progressspinner";
import {CommonModule} from "@angular/common";
import {UserSyncService} from "../../../services/user-sync.service";
import {UserSyncQueueStateDto, UserSyncQueueType} from "admin-api-dto";
import {finalize} from "rxjs";

const QUEUE_LABELS: Record<UserSyncQueueType, string> = {
  scheduled: "Scheduled queue",
  priority: "Priority queue",
};

@Component({
  selector: 'app-user-sync-tab',
  standalone: true,
  imports: [
    ButtonModule,
    CommonModule,
    ProgressSpinner,
    TableModule,
    Tag,
  ],
  templateUrl: './user-sync-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserSyncTabComponent implements OnInit {
  protected readonly queueStates = signal<UserSyncQueueStateDto[]>([]);
  protected readonly loading = signal(true);
  protected readonly togglingQueue = signal<UserSyncQueueType | null>(null);

  constructor(private readonly userSyncService: UserSyncService, private readonly messageService: MessageService) {
  }

  ngOnInit(): void {
    this.reloadQueues();
  }

  protected readonly queueLabels = QUEUE_LABELS;

  reloadQueues() {
    this.loading.set(true);
    this.userSyncService.getQueues().pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: queues => {
        this.queueStates.set([queues.scheduled, queues.priority]);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Unable to refresh user sync queues'
        });
      }
    });
  }

  labelFor(queue: UserSyncQueueType): string {
    return QUEUE_LABELS[queue];
  }

  toggleListener(state: UserSyncQueueStateDto) {
    const target = state.queue;
    const enable = !state.listenerEnabled;
    this.togglingQueue.set(target);
    this.userSyncService.toggleListener(target, enable).pipe(
      finalize(() => this.togglingQueue.set(null))
    ).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: `Listener ${enable ? 'resumed' : 'paused'}`,
          detail: QUEUE_LABELS[target]
        });
        this.reloadQueues();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: `Failed to ${enable ? 'resume' : 'pause'} listener`,
          detail: QUEUE_LABELS[target]
        });
      }
    });
  }

  isToggling(queue: UserSyncQueueType): boolean {
    return this.togglingQueue() === queue;
  }
}
